'use client';

import type { FC } from 'react';
import { ScreenContent, ScreenFixedTop, ScreenTabBar } from '../../screen';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useLocalizedHref } from '../../hooks/useLocalizedHref';
import { InfoCard } from '../../components/ui/InfoCard';
import translations from './RefundTermsScreen.translations';

const REFUND_POLICY_URL = 'https://telegra.ph/REFUND-POLICY-SHOT-06-28';

const NumberedList: FC<{ items: string[] }> = ({ items }) => (
  <ol className="m-0 mt-2 flex list-decimal flex-col gap-1.5 ps-5">
    {items.map((item, i) => (
      <li key={i} className="text-sm leading-normal text-[rgb(209,213,219)]">
        {item}
      </li>
    ))}
  </ol>
);

const Card: FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <InfoCard>
    <h2 className="m-0 mb-2 text-base font-bold text-white">{title}</h2>
    {children}
  </InfoCard>
);

export const RefundTermsScreen: FC = () => {
  const { t } = useLocalTranslation(translations);
  const localize = useLocalizedHref();

  return (
    <>
      <ScreenFixedTop title={t('title')} />
      <ScreenTabBar />
      <ScreenContent>
        <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4 pb-8">
          <Card title={t('sec1Title')}>
            <p className="m-0 text-sm leading-normal text-[rgb(209,213,219)]">{t('sec1Intro')}</p>
            <NumberedList
              items={[t('sec1Item1'), t('sec1Item2'), t('sec1Item3'), t('sec1Item4')]}
            />
            <p className="m-0 mt-3 text-sm leading-normal text-label-tertiary">{t('sec1Note')}</p>
          </Card>

          <Card title={t('sec2Title')}>
            <p className="m-0 text-sm leading-normal text-[rgb(209,213,219)]">{t('sec2Intro')}</p>
            <NumberedList
              items={[
                t('sec2Item1'),
                t('sec2Item2'),
                t('sec2Item3'),
                t('sec2Item4'),
                t('sec2Item5'),
                t('sec2Item6'),
                t('sec2Item7'),
              ]}
            />
          </Card>

          <Card title={t('sec3Title')}>
            <p className="m-0 text-sm leading-normal text-[rgb(209,213,219)]">
              {t('sec3Before')}
              <a className="text-accent-blue hover:underline" href={localize('/support')}>
                {t('sec3LinkWord')}
              </a>
              {t('sec3After')}
            </p>
          </Card>

          <Card title={t('sec4Title')}>
            <p className="m-0 text-sm leading-normal text-[rgb(209,213,219)]">{t('sec4Text')}</p>
          </Card>

          <a
            className="text-sm font-medium text-accent-blue hover:underline"
            href={REFUND_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('policyLink')} →
          </a>
        </div>
      </ScreenContent>
    </>
  );
};
